import React, { useLayoutEffect, useRef, useState } from 'react';

export const NAME_SEPARATOR = ', ';

/**
 * How many names, from the first, fit in `available` with a "+N" count for the rest
 * (`countWidth`, its gap included). At least one shows, truncated when even it does not fit.
 */
export const countNamesThatFit = ({ nameWidths, separatorWidth, countWidth, available }) => {
  const total = nameWidths.length;
  let width =
    nameWidths.reduce((sum, nameWidth) => sum + nameWidth, 0) +
    separatorWidth * Math.max(0, total - 1);
  if (width <= available) return total;

  for (let shown = total - 1; shown > 1; shown -= 1) {
    width -= nameWidths[shown] + separatorWidth;
    if (width + countWidth <= available) return shown;
  }
  return Math.min(total, 1);
};

const textWidths = (element, texts) => {
  const style = window.getComputedStyle(element);
  const ruler = document.createElement('span');
  Object.assign(ruler.style, {
    position: 'absolute',
    visibility: 'hidden',
    whiteSpace: 'pre',
    font: style.font,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
  });
  document.body.appendChild(ruler);
  const widths = texts.map(text => {
    ruler.textContent = text;
    return ruler.getBoundingClientRect().width;
  });
  ruler.remove();
  return widths;
};

// A container with no width yet (hidden, or not laid out) shows every name.
const measure = (container, names) => {
  if (!container?.clientWidth) return names.length;
  const [separatorWidth, countWidth, ...nameWidths] = textWidths(container, [
    NAME_SEPARATOR,
    `+${names.length}`,
    ...names,
  ]);
  const gap = parseFloat(window.getComputedStyle(container).columnGap) || 0;
  return countNamesThatFit({
    nameWidths,
    separatorWidth,
    countWidth: countWidth + gap,
    available: container.clientWidth,
  });
};

/**
 * Names comma-separated on one line: as many as fit, then "+N" for the rest. The count sits
 * outside the truncating span so it never gets cut off.
 */
const PickedNames = ({ names }) => {
  const containerRef = useRef(null);
  const namesRef = useRef(names);
  namesRef.current = names;
  const [shown, setShown] = useState(names.length);
  const key = names.join('\u0000');

  // Measured before paint, so a new pick never shows a frame with every name first.
  useLayoutEffect(() => {
    setShown(measure(containerRef.current, namesRef.current));
  }, [key]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(() => setShown(measure(container, namesRef.current)));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const visible = Math.min(shown, names.length);
  const rest = names.length - visible;

  return (
    <span ref={containerRef} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
      <span className="truncate">{names.slice(0, visible).join(NAME_SEPARATOR)}</span>
      {rest > 0 && <span className="shrink-0 text-muted-foreground">+{rest}</span>}
    </span>
  );
};

export default PickedNames;
